import { CaseModel } from 'models/CaseModel';
import { TaskModel } from 'models/TaskModel';

import { ApiRouteService } from '../services';
import { HttpRequestOptionsType } from '../types';
// Assuming this path is correct
import { HttpClient } from './HttpClient';

export class CaseClient {
  private readonly httpClient: HttpClient;
  private readonly baseUrl = `${ApiRouteService.getCompumedApiBaseRoute()}/api/cases`;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  public async getCases(patientId: number, options?: HttpRequestOptionsType): Promise<CaseModel[]> {
    const requestOptions = await this.httpClient.createStandardOptions('GET', null, options);
    const request = fetch(`${this.baseUrl}?patientId=${patientId}`, requestOptions);
    return this.httpClient.fetchAndParse<CaseModel[]>(request);
  }

  public async getCase(caseId: number, options?: HttpRequestOptionsType): Promise<CaseModel> {
    const requestOptions = await this.httpClient.createStandardOptions('GET', null, options);
    const request = fetch(`${this.baseUrl}/${caseId}`, requestOptions);
    return this.httpClient.fetchAndParse<CaseModel>(request);
  }

  public async createCase(caseData: Omit<CaseModel, 'id' | 'dateCreated' | 'lastUpdated'>, options?: HttpRequestOptionsType): Promise<CaseModel> {
    const requestOptions = await this.httpClient.createStandardOptions('POST', caseData, options);
    const request = fetch(this.baseUrl, requestOptions);
    return this.httpClient.fetchAndParse<CaseModel>(request);
  }

  public async updateCase(caseData: CaseModel, options?: HttpRequestOptionsType): Promise<CaseModel> {
    const requestOptions = await this.httpClient.createStandardOptions('PUT', caseData, options);
    const request = fetch(`${this.baseUrl}/${caseData.id}`, requestOptions);
    // Assuming update returns the updated case, adjust if it returns NoContent
    return this.httpClient.fetchAndParse<CaseModel>(request);
  }

  public async deleteCase(caseId: number, options?: HttpRequestOptionsType): Promise<void> {
    const requestOptions = await this.httpClient.createStandardOptions('DELETE', null, options);
    const request = fetch(`${this.baseUrl}/${caseId}`, requestOptions);
    // Use fetch for NoContent responses
    await this.httpClient.fetch(request);
  }

  // --- Task Methods ---

  public async getCaseTasks(caseId: number, options?: HttpRequestOptionsType): Promise<TaskModel[]> {
    const requestOptions = await this.httpClient.createStandardOptions('GET', null, options);
    const request = fetch(`${this.baseUrl}/${caseId}/tasks`, requestOptions);
    return this.httpClient.fetchAndParse<TaskModel[]>(request);
  }

  public async createTask(taskData: Omit<TaskModel, 'id' | 'dateCreated' | 'lastUpdated'>, options?: HttpRequestOptionsType): Promise<TaskModel> {
    const requestOptions = await this.httpClient.createStandardOptions('POST', taskData, options);
    const request = fetch(`${this.baseUrl}/${taskData.caseId}/tasks`, requestOptions);
    return this.httpClient.fetchAndParse<TaskModel>(request);
  }

  public async updateTask(taskData: TaskModel, options?: HttpRequestOptionsType): Promise<TaskModel> {
    const requestOptions = await this.httpClient.createStandardOptions('PUT', taskData, options);
    const request = fetch(`${this.baseUrl}/${taskData.caseId}/tasks/${taskData.id}`, requestOptions);
    // Assuming update returns the updated task
    return this.httpClient.fetchAndParse<TaskModel>(request);
  }

  public async deleteTask(caseId: number, taskId: number, options?: HttpRequestOptionsType): Promise<void> {
    // Adjusted to require caseId for the standard RESTful path
    const requestOptions = await this.httpClient.createStandardOptions('DELETE', null, options);
    const request = fetch(`${this.baseUrl}/${caseId}/tasks/${taskId}`, requestOptions);
    await this.httpClient.fetch(request);
  }

  // TODO: Add methods for adding/removing files and exams to/from a case if needed
}
